import React, { useEffect, useMemo } from "react";
import { AlertCircle, CheckCircle } from "lucide-react";
import { PERMISSIONS } from "../../utils/rbac/permissions";
import { RoleTypeEnum } from "../../utils/constants";
import { getDisplayName } from "../../utils/nameUtils";
import {
  AttendanceLegend,
  AttendanceTable,
  useAttendance,
  UserAttendanceData,
} from "./index";
import { AttendanceTableSkeleton } from "./Skeleton";
import { useAuth } from "../../store/hooks/useAuth";
import {
  ReportTabPanelProps,
  useRegisterExport,
} from "../reports/reportTabTypes";

const AttendanceReports: React.FC<ReportTabPanelProps> = ({
  onStatsChange,
  registerExport,
  period,
}) => {
  const { user } = useAuth();
  const isHrUser = user?.department === RoleTypeEnum.HR;
  const isAdminRole =
    user?.role?.toLowerCase() === RoleTypeEnum.ADMIN?.toLowerCase() || user?.role?.toLowerCase() === RoleTypeEnum.SUPER_ADMIN?.toLowerCase();
  const isAdminView = Boolean(isHrUser || isAdminRole);

  const isManagerRole =
    user?.role?.toLowerCase() === RoleTypeEnum.MANAGER?.toLowerCase();

  const isManagerView = !isAdminView && isManagerRole;

  const {
    holidays,
    loading,
    error,
    success,
    filters,
    handleFilterChange,
    employeeSearch,
    setEmployeeSearch,
    pagination,
    handlePageChange,
    handleItemsPerPageChange,
    hasAdminAccess,
    hasManagerAccess,
    downloadReportExcel,
    // User-centric data from backend (each user has their attendance data embedded)
    users,
  } = useAttendance({
    isAdmin: isAdminView,
    isManager: isManagerView,
  });

  useEffect(() => {
    if (!period?.year) return;
    if (period.month) handleFilterChange("month", period.month);
    handleFilterChange("year", period.year);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period?.year, period?.month]);

  const currentUserData = useMemo<UserAttendanceData | undefined>(() => {
    if (!user?.id) return undefined;
    return {
      _id: user.id,
      name: getDisplayName(user),
      workEmail: user.workEmail,
      department: user.department || "",
      employeeId: user.employeeId,
      status: "active",
      attendanceStatus: {},
      summary: {
        totalDays: 0,
        presentDays: 0,
        absentDays: 0,
        halfDays: 0,
        leaveDays: 0,
        totalLeaves: 0,
        totalWorkHours: 0,
        totalBreakHours: 0,
        netWorkHours: 0,
      },
      attendanceRecords: [],
    };
  }, [user]);

  // Users are already filtered and paginated by the backend
  // Only need to move current user to top if needed
  const scopedUsers = useMemo(() => {
    // Use backend users if available
    let baseList: typeof users;
    if (users.length > 0) {
      baseList = [...users];
    } else {
      baseList = [];
    }

    // Move current user to top of list
    if (user?.id && baseList.length > 1 && user.role !== RoleTypeEnum.ADMIN) {
      const currentUserIndex = baseList.findIndex(
        (u) => u._id === user.id
      );
      if (currentUserIndex > 0) {
        const [currentUserRecord] = baseList.splice(currentUserIndex, 1);
        return [currentUserRecord, ...baseList];
      }
    }

    return baseList;
  }, [users, currentUserData, user?.id]);

  const canViewMultiple = hasAdminAccess || hasManagerAccess;

  const handleExport = async () => {
    try {
      await downloadReportExcel();
    } catch {
      // Error already logged in useAttendance
    }
  };

  useRegisterExport(handleExport, registerExport);

  const summaryValues = useMemo(() => {
    let totalPresent = 0;
    let totalAbsent = 0;
    let totalWorkingDays = 0;

    for (const u of scopedUsers) {
      if (u.summary) {
        totalPresent += u.summary.presentDays ?? 0;
        totalAbsent += u.summary.absentDays ?? 0;
        totalWorkingDays += u.summary.totalDays ?? 0;
      }
    }

    const attendanceRate =
      totalWorkingDays > 0
        ? Number(((totalPresent / totalWorkingDays) * 100).toFixed(1))
        : 0;

    return {
      totalWorkingDays,
      totalPresent,
      totalAbsent,
      attendanceRate,
    };
  }, [scopedUsers]);

  useEffect(() => {
    onStatsChange?.(summaryValues);
  }, [summaryValues, onStatsChange]);

  return (
    <div>
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center">
          <CheckCircle className="w-5 h-5 mr-2" />
          {success}
        </div>
      )}

      {loading ? (
        <AttendanceTableSkeleton />
      ) : (
        <AttendanceTable
          users={scopedUsers}
          holidays={holidays}
          filters={filters}
          loading={loading}
          isAdmin={canViewMultiple}
          pagination={canViewMultiple ? pagination : undefined}
          onPageChange={canViewMultiple ? handlePageChange : undefined}
          onItemsPerPageChange={
            canViewMultiple ? handleItemsPerPageChange : undefined
          }
          currentUser={currentUserData}
          search={canViewMultiple ? employeeSearch : undefined}
          onSearchChange={canViewMultiple ? setEmployeeSearch : undefined}
        />
      )}

      <AttendanceLegend />
    </div>
  );
};

export default AttendanceReports;